import { useCallback, useRef, useState } from 'react';
import { cloneDeep } from 'lodash';
import BaseGraph from '../model';
import { PedigreeCondition } from '../types';

// -----------------------------------------------------------------------------
interface PedigreeState {
  baseGraph: BaseGraph;
  extraData: {
    conditions: PedigreeCondition[];
  };
  timestamp: number;
}

// -----------------------------------------------------------------------------
export const useUndoRedo = (maxHistorySize: number = 10) => {
  const undoStack = useRef<PedigreeState[]>([]);
  const redoStack = useRef<PedigreeState[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // ---------------------------------------------------------------------------
  const cloneBaseGraph = useCallback((graph: BaseGraph): BaseGraph => {
    const clonedGraph = cloneDeep(graph);

    const newGraph = new BaseGraph();
    newGraph.v = clonedGraph.v;
    newGraph.inedges = clonedGraph.inedges;
    newGraph.weights = clonedGraph.weights;
    newGraph.type = clonedGraph.type;
    newGraph.properties = clonedGraph.properties;
    newGraph.vWidth = clonedGraph.vWidth;
    newGraph.maxRealVertexId = clonedGraph.maxRealVertexId;

    return newGraph;
  }, []);

  // ---------------------------------------------------------------------------
  const cloneExtraData = useCallback(
    (extraData: { conditions: PedigreeCondition[] }) => {
      return cloneDeep(extraData);
    },
    []
  );

  // ---------------------------------------------------------------------------
  const updateFlags = useCallback(() => {
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(redoStack.current.length > 0);
  }, []);

  // ---------------------------------------------------------------------------
  const pushState = useCallback(
    (baseGraph: BaseGraph, extraData: { conditions: PedigreeCondition[] }) => {
      const state: PedigreeState = {
        baseGraph: cloneBaseGraph(baseGraph),
        extraData: cloneExtraData(extraData),
        timestamp: Date.now(),
      };

      undoStack.current.push(state);

      if (undoStack.current.length > maxHistorySize) {
        undoStack.current.shift();
      }

      redoStack.current = [];

      updateFlags();
    },
    [cloneBaseGraph, cloneExtraData, maxHistorySize, updateFlags]
  ); // ---------------------------------------------------------------------------
  const undo = useCallback(
    (
      currentBaseGraph: BaseGraph,
      currentExtraData: { conditions: PedigreeCondition[] }
    ): PedigreeState | null => {
      if (undoStack.current.length === 0) {
        return null;
      }

      const stateToRestore = undoStack.current.pop();
      if (!stateToRestore) {
        return null;
      }

      const currentState: PedigreeState = {
        baseGraph: cloneBaseGraph(currentBaseGraph),
        extraData: cloneExtraData(currentExtraData),
        timestamp: Date.now(),
      };

      redoStack.current.push(currentState);

      if (redoStack.current.length > maxHistorySize) {
        redoStack.current.shift();
      }

      updateFlags();

      return {
        baseGraph: cloneBaseGraph(stateToRestore.baseGraph),
        extraData: cloneExtraData(stateToRestore.extraData),
        timestamp: stateToRestore.timestamp,
      };
    },
    [cloneBaseGraph, cloneExtraData, maxHistorySize, updateFlags]
  );

  // ---------------------------------------------------------------------------
  const redo = useCallback(
    (
      currentBaseGraph: BaseGraph,
      currentExtraData: { conditions: PedigreeCondition[] }
    ): PedigreeState | null => {
      if (redoStack.current.length === 0) {
        return null;
      }

      const stateToRedo = redoStack.current.pop();
      if (!stateToRedo) {
        return null;
      }

      const currentState: PedigreeState = {
        baseGraph: cloneBaseGraph(currentBaseGraph),
        extraData: cloneExtraData(currentExtraData),
        timestamp: Date.now(),
      };

      undoStack.current.push(currentState);

      if (undoStack.current.length > maxHistorySize) {
        undoStack.current.shift();
      }

      updateFlags();

      return {
        baseGraph: cloneBaseGraph(stateToRedo.baseGraph),
        extraData: cloneExtraData(stateToRedo.extraData),
        timestamp: stateToRedo.timestamp,
      };
    },
    [cloneBaseGraph, cloneExtraData, maxHistorySize, updateFlags]
  );

  // ---------------------------------------------------------------------------
  const clearHistory = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    updateFlags();
  }, [updateFlags]);

  // ---------------------------------------------------------------------------
  const getHistorySize = useCallback(() => {
    return {
      undoCount: undoStack.current.length,
      redoCount: redoStack.current.length,
    };
  }, []);

  // ---------------------------------------------------------------------------
  return {
    canUndo,
    canRedo,
    undo,
    redo,
    pushState,
    clearHistory,
    getHistorySize,
  };
};
